import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  FormField,
  TextField,
  FlexColumn,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import useCodeRepositoryIntegration from 'shared/hooks/integrations/useCodeRepositoryIntegration';
import useNango from 'shared/hooks/useNango';
import * as codeProjectsApi from 'shared/services/codeProjectsApi';
import { VersionControl } from 'shared/types';
import { isValidUrl } from 'shared/utils/validators';
import { IntegrationError } from '../../../../types';
import { parseIntegrationError } from '../../../../utils';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import ProviderSelection from './ProviderSelection';
import TokenAuthSection from './TokenAuthSection';
import DbtProjectsConfigSection from './DbtProjectsConfigSection';
import SettingsSection from './SettingsSection';

enum DialogStep {
  vcsSelect,
  orgUrl, // Azure DevOps only
  auth,
  config,
}

type OrgUrlForm = {
  orgUrl: string;
};

type TokenForm = {
  token: string;
  enterpriseUrl: string | null;
};

type SharedTokenForm = {
  sharedToken: string;
};

type ProjectForm = {
  repository: string;
  branch: string;
  projectDir: string;
};

type SettingsForm = {
  addLabels: boolean;
  branchPrefix: string;
  commitMessagePrefix: string;
  requireTicketId: boolean;
};

const DEFAULT_ADD_LABELS = true;
const DEFAULT_BRANCH_PREFIX = '';
const DEFAULT_COMMIT_MESSAGE_PREFIX = '';
const DEFAULT_REQUIRE_TICKET_ID = false;

type Props = {
  open: boolean;
  onClose: () => void;
  envId: string;
  appInstalled: VersionControl | null;
  error?: IntegrationError;
};

const RepositoryDialog = ({
  open,
  onClose,
  envId,
  appInstalled,
  error: propError,
}: Props) => {
  const [selectedVersionControl, setSelectedVersionControl] =
    useState<VersionControl>();
  const [isNewConnection, setIsNewConnection] = useState(false);
  const [step, setStep] = useState<DialogStep>(DialogStep.vcsSelect);
  const { nangoAuth, success: nangoSuccess, error: nangoError } = useNango();

  const {
    dbtProjects,
    existingCodeProjects,
    additionalSettings,
    codeProjectType,
    codeProjectsMutation,
    isLoading: isDataLoading,
  } = useCodeRepositoryIntegration({
    envId,
    onUpdateSuccess: () => {
      onClose();
    },
    fetchFullData: true,
  });

  const [editEnterpriseUrl, setEditEnterpriseUrl] = useState<
    string | undefined
  >(undefined);

  const settingsForm = useForm<SettingsForm>({
    defaultValues: {
      addLabels: DEFAULT_ADD_LABELS,
      branchPrefix: DEFAULT_BRANCH_PREFIX,
      commitMessagePrefix: DEFAULT_COMMIT_MESSAGE_PREFIX,
      requireTicketId: DEFAULT_REQUIRE_TICKET_ID,
    },
  });

  const error = useMemo(() => {
    if (propError) return propError;
    if (codeProjectsMutation.error) {
      return parseIntegrationError(codeProjectsMutation.error);
    }
    return undefined;
  }, [propError, codeProjectsMutation.error]);

  const {
    mutate: checkToken,
    isSuccess: gitTokenSuccess,
    isLoading: gitTokenLoading,
    error: gitTokenError,
  } = useMutation((tokenForm: TokenForm) => {
    if (!selectedVersionControl) {
      throw new Error('No version control selected');
    }
    return codeProjectsApi.testGitAccessToken(envId, {
      code_project_type: selectedVersionControl,
      token: tokenForm.token,
      additional_auth_settings:
        selectedVersionControl === 'AzureDevOps'
          ? {
              org_url: tokenForm.enterpriseUrl,
            }
          : ['GitHub', 'GitLab'].includes(selectedVersionControl) &&
            tokenForm.enterpriseUrl != null &&
            tokenForm.enterpriseUrl !== ''
          ? {
              enterprise_url: tokenForm.enterpriseUrl,
            }
          : undefined,
    });
  });

  const {
    register: registerOrgUrl,
    handleSubmit: handleSubmitOrgUrl,
    formState: { errors: errorsOrgUrl, isValid: isValidOrgUrl },
    watch: watchOrgUrl,
    reset: resetOrgUrl,
  } = useForm<OrgUrlForm>({ mode: 'onChange' });

  const {
    register: registerAuth,
    handleSubmit: handleSubmitAuth,
    formState: { errors: errorsAuth, isValid: isValidAuth },
    watch: watchAuth,
    reset: resetAuth,
  } = useForm<TokenForm>();

  const sharedTokenForm = useForm<SharedTokenForm>({
    mode: 'onChange',
  });

  const [projectFormsData, setProjectFormsData] = useState<
    Map<string, { isValid: boolean; isDirty: boolean; data: ProjectForm }>
  >(new Map());

  const handleProjectFormChange = useCallback(
    (
      dbtProjectId: string,
      isValid: boolean,
      isDirty: boolean,
      data: ProjectForm,
    ) => {
      setProjectFormsData((prev) => {
        const updated = new Map(prev);
        updated.set(dbtProjectId, { isValid, isDirty, data });
        return updated;
      });
    },
    [],
  );

  const isEdit = existingCodeProjects.length > 0;

  useEffect(() => {
    settingsForm.reset({
      addLabels: additionalSettings?.add_labels ?? DEFAULT_ADD_LABELS,
      branchPrefix: additionalSettings?.branch_prefix ?? DEFAULT_BRANCH_PREFIX,
      commitMessagePrefix:
        additionalSettings?.commit_message_prefix ??
        DEFAULT_COMMIT_MESSAGE_PREFIX,
      requireTicketId:
        additionalSettings?.require_ticket_id ?? DEFAULT_REQUIRE_TICKET_ID,
    });
  }, [additionalSettings, settingsForm]);

  const isEnterpriseUrlDirty = useMemo(() => {
    const existingUrl = additionalSettings?.enterprise_url ?? '';
    return editEnterpriseUrl !== undefined && editEnterpriseUrl !== existingUrl;
  }, [editEnterpriseUrl, additionalSettings]);

  const isEnterpriseUrlValid = useMemo(() => {
    if (!editEnterpriseUrl) return true;
    return isValidUrl(editEnterpriseUrl);
  }, [editEnterpriseUrl]);

  const areAllProjectFormsValid = useMemo(() => {
    if (projectFormsData.size === 0) return false;

    const forms = Array.from(projectFormsData.values());

    if (isEdit) {
      if (!isEnterpriseUrlValid) return false;

      const projectsToValidate = forms.filter((form) => {
        const hasDataNow =
          (form.data?.repository?.trim() ?? '') !== '' ||
          (form.data?.branch?.trim() ?? '') !== '' ||
          (form.data?.projectDir?.trim() ?? '') !== '';

        return hasDataNow || form.isDirty;
      });

      if (projectsToValidate.length === 0 && !isEnterpriseUrlDirty)
        return false;

      const allValid = projectsToValidate.every((form) => form.isValid);
      const anyDirty =
        forms.some((form) => form.isDirty) ||
        sharedTokenForm.formState.isDirty ||
        isEnterpriseUrlDirty ||
        settingsForm.formState.isDirty;

      return allValid && anyDirty;
    }

    // For new connections, at least one form must have data and be valid
    const formsWithAnyData = forms.filter(
      (form) =>
        (form.data?.repository?.trim() ?? '') !== '' ||
        (form.data?.branch?.trim() ?? '') !== '' ||
        (form.data?.projectDir?.trim() ?? '') !== '',
    );

    if (formsWithAnyData.length === 0) return false;

    return formsWithAnyData.every((form) => form.isValid);
  }, [
    projectFormsData,
    isEdit,
    sharedTokenForm.formState.isDirty,
    isEnterpriseUrlDirty,
    isEnterpriseUrlValid,
    settingsForm.formState.isDirty,
  ]);

  const handleOrgUrlSubmit = handleSubmitOrgUrl(() => {
    if (appInstalled) {
      setStep(DialogStep.config);
    } else {
      setStep(DialogStep.auth);
    }
  });

  const handleAuthSubmit = handleSubmitAuth((formData) => {
    const orgUrl = watchOrgUrl('orgUrl');
    if (orgUrl) {
      formData.enterpriseUrl = orgUrl;
    }

    checkToken(formData);
  });

  const handleConfigSubmit = () => {
    if (!selectedVersionControl) return;

    const sharedToken = sharedTokenForm.getValues('sharedToken');
    const authToken = watchAuth('token');
    const orgUrl = watchOrgUrl('orgUrl');
    const enterpriseUrl = watchAuth('enterpriseUrl');

    const token = sharedToken || authToken || null;

    const projectsData = new Map<
      string,
      {
        repository: string;
        branch: string;
        projectDir: string;
      }
    >();

    let hasProjectChanges = false;

    projectFormsData.forEach((formState, dbtProjectId) => {
      const repository = formState.data?.repository ?? '';
      const isFilled = repository.trim() !== '';

      if (isFilled && formState.isValid) {
        projectsData.set(dbtProjectId, {
          repository,
          branch: formState.data?.branch ?? '',
          projectDir: formState.data?.projectDir ?? '',
        });
      }

      if (formState.isDirty) {
        hasProjectChanges = true;
      }
    });

    const resolvedEnterpriseUrl = isEdit
      ? isEnterpriseUrlDirty
        ? editEnterpriseUrl || null
        : undefined
      : enterpriseUrl || null;

    const { addLabels, branchPrefix, commitMessagePrefix, requireTicketId } =
      settingsForm.getValues();

    codeProjectsMutation.mutate({
      versionControl: selectedVersionControl,
      sharedToken: token,
      orgUrl: isEdit ? undefined : orgUrl || null,
      enterpriseUrl: resolvedEnterpriseUrl,
      addLabels,
      branchPrefix,
      commitMessagePrefix,
      requireTicketId,
      projectsData,
      hasProjectChanges,
    });
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    if (step === DialogStep.orgUrl) {
      handleOrgUrlSubmit();
    } else if (step === DialogStep.auth) {
      handleAuthSubmit();
    } else if (step === DialogStep.config) {
      handleConfigSubmit();
    }
  };

  const resetForm = useCallback(() => {
    setIsNewConnection(false);

    if (appInstalled || codeProjectType) {
      setSelectedVersionControl(
        appInstalled || (codeProjectType as VersionControl),
      );
      setStep(DialogStep.config);
    } else {
      setSelectedVersionControl(undefined);
      setStep(DialogStep.vcsSelect);
    }

    resetAuth({ token: '', enterpriseUrl: null });
    resetOrgUrl({ orgUrl: '' });
    sharedTokenForm.reset({ sharedToken: '' });
    settingsForm.reset({
      addLabels: additionalSettings?.add_labels ?? DEFAULT_ADD_LABELS,
      branchPrefix: additionalSettings?.branch_prefix ?? DEFAULT_BRANCH_PREFIX,
      commitMessagePrefix:
        additionalSettings?.commit_message_prefix ??
        DEFAULT_COMMIT_MESSAGE_PREFIX,
      requireTicketId:
        additionalSettings?.require_ticket_id ?? DEFAULT_REQUIRE_TICKET_ID,
    });
    setProjectFormsData(new Map());
    setEditEnterpriseUrl(undefined);
  }, [
    appInstalled,
    codeProjectType,
    resetAuth,
    resetOrgUrl,
    sharedTokenForm,
    settingsForm,
    additionalSettings,
  ]);

  const selectProvider = useCallback(
    (value: VersionControl) => {
      setSelectedVersionControl(value);
      if (value === 'AzureDevOps') {
        setStep(DialogStep.orgUrl);
      } else if (appInstalled) {
        setStep(DialogStep.config);
      } else {
        setStep(DialogStep.auth);
      }
    },
    [appInstalled],
  );

  useEffect(() => {
    if (nangoSuccess) {
      setIsNewConnection(true);
      setStep(DialogStep.config);
    }
  }, [nangoSuccess]);

  useEffect(() => {
    if (watchAuth('token') && gitTokenSuccess) {
      setIsNewConnection(true);
      setStep(DialogStep.config);
    }
  }, [gitTokenSuccess, watchAuth]);

  useEffect(() => {
    if (codeProjectType && !selectedVersionControl) {
      setSelectedVersionControl(codeProjectType as VersionControl);
      setStep(DialogStep.config);
    }
  }, [codeProjectType, selectedVersionControl]);

  const getTitle = () => {
    switch (step) {
      case DialogStep.vcsSelect:
        return 'Connect code repository';
      case DialogStep.orgUrl:
        return 'Azure DevOps organization URL';
      case DialogStep.auth:
        return 'Connect code repository';
      case DialogStep.config:
        if (isEdit) {
          return 'Edit code repository details';
        }
        return !appInstalled && !isNewConnection
          ? 'Connection method'
          : 'Configure code repository';
      default:
        return 'Connect code repository';
    }
  };

  const getDescription = () => {
    switch (step) {
      case DialogStep.orgUrl:
        return 'Enter the URL of your Azure DevOps organization.';
      case DialogStep.auth:
        return 'Create and sync configuration as code in your project.';
      default:
        return undefined;
    }
  };

  const isValid = () => {
    switch (step) {
      case DialogStep.vcsSelect:
        return true;
      case DialogStep.orgUrl:
        return isValidOrgUrl;
      case DialogStep.auth:
        return isValidAuth;
      case DialogStep.config:
        return areAllProjectFormsValid;
      default:
        return false;
    }
  };

  const getSaveButtonText = () => {
    if (step === DialogStep.auth) {
      return 'Connect with token';
    }
    if (step === DialogStep.config) {
      return 'Save';
    }
    return 'Continue';
  };

  const nangoProviderId =
    selectedVersionControl === 'AzureDevOps'
      ? 'azure-devops'
      : selectedVersionControl?.toLowerCase();

  const docsLink =
    step === DialogStep.auth
      ? 'https://docs.elementary-data.com/cloud/integrations/code-repo/connect-code-repo'
      : undefined;

  return (
    <IntegrationDialog
      open={open}
      title={getTitle()}
      description={getDescription()}
      onClose={onClose}
      onSubmit={handleSubmit}
      isValid={isValid()}
      docsLink={docsLink}
      error={step === DialogStep.config ? undefined : error}
      reset={resetForm}
      loading={codeProjectsMutation.isLoading || gitTokenLoading}
      saveButtonText={getSaveButtonText()}
    >
      <FlexColumn sx={{ gap: 3 }}>
        {step === DialogStep.vcsSelect && (
          <ProviderSelection onSelected={selectProvider} />
        )}

        {nangoSuccess && (
          <Alert severity="success" style={{ fontWeight: 'bold' }}>
            Elementary {selectedVersionControl} app was successfully connected
          </Alert>
        )}

        {step === DialogStep.orgUrl && (
          <FormField errorMessage={errorsOrgUrl.orgUrl?.message}>
            <TextField
              {...registerOrgUrl('orgUrl', {
                required: requiredLabel,
                validate: (value) =>
                  isValidUrl(value) ? true : 'This is not a valid url',
              })}
              placeholder="Org URL"
              variant="outlined"
              fullWidth
              size="large"
            />
          </FormField>
        )}

        {step === DialogStep.auth && (
          <TokenAuthSection
            selectedVersionControl={selectedVersionControl!}
            nangoProviderId={nangoProviderId}
            nangoAuth={nangoAuth}
            register={registerAuth}
            errors={errorsAuth}
            gitTokenError={gitTokenError}
            nangoError={nangoError}
          />
        )}

        {step === DialogStep.config && dbtProjects.length > 0 && (
          <DbtProjectsConfigSection
            dbtProjects={dbtProjects}
            selectedVersionControl={selectedVersionControl}
            sharedTokenForm={sharedTokenForm}
            existingCodeProjects={existingCodeProjects}
            onProjectFormChange={handleProjectFormChange}
            isNewConnection={isNewConnection}
            error={error}
            isLoading={codeProjectsMutation.isLoading}
            enterpriseUrl={
              editEnterpriseUrl ?? additionalSettings?.enterprise_url ?? ''
            }
            onEnterpriseUrlChange={setEditEnterpriseUrl}
            addLabels={settingsForm.watch('addLabels')}
            onAddLabelsChange={(v) =>
              settingsForm.setValue('addLabels', v, { shouldDirty: true })
            }
            branchPrefix={settingsForm.watch('branchPrefix')}
            onBranchPrefixChange={(v) =>
              settingsForm.setValue('branchPrefix', v, { shouldDirty: true })
            }
            commitMessagePrefix={settingsForm.watch('commitMessagePrefix')}
            onCommitMessagePrefixChange={(v) =>
              settingsForm.setValue('commitMessagePrefix', v, {
                shouldDirty: true,
              })
            }
            requireTicketId={settingsForm.watch('requireTicketId')}
            onRequireTicketIdChange={(v) =>
              settingsForm.setValue('requireTicketId', v, {
                shouldDirty: true,
              })
            }
          />
        )}

        {step === DialogStep.config &&
          !isDataLoading &&
          dbtProjects.length === 0 && (
            <FlexColumn sx={{ gap: 3 }}>
              <Alert severity="warning">
                No dbt projects found in this environment. Please add a dbt
                project first.
              </Alert>
              <SettingsSection
                selectedVersionControl={selectedVersionControl}
                addLabels={settingsForm.watch('addLabels')}
                onAddLabelsChange={(v) =>
                  settingsForm.setValue('addLabels', v, { shouldDirty: true })
                }
                branchPrefix={settingsForm.watch('branchPrefix')}
                onBranchPrefixChange={(v) =>
                  settingsForm.setValue('branchPrefix', v, {
                    shouldDirty: true,
                  })
                }
                commitMessagePrefix={settingsForm.watch('commitMessagePrefix')}
                onCommitMessagePrefixChange={(v) =>
                  settingsForm.setValue('commitMessagePrefix', v, {
                    shouldDirty: true,
                  })
                }
                requireTicketId={settingsForm.watch('requireTicketId')}
                onRequireTicketIdChange={(v) =>
                  settingsForm.setValue('requireTicketId', v, {
                    shouldDirty: true,
                  })
                }
                isLoading={codeProjectsMutation.isLoading}
              />
            </FlexColumn>
          )}
      </FlexColumn>
    </IntegrationDialog>
  );
};

export default RepositoryDialog;
